import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { GrowthBook, GrowthBookProvider as GBProvider } from '@growthbook/growthbook-react';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { useCurrentOrg } from 'new/hooks/adapters/useCurrentOrg';
import { setGlobalGrowthBookInstance } from 'new/constants/featureFlags';
import AnalyticsUtils from 'js/lib/AnalyticsUtils';
import {
  GROWTHBOOK_API_HOST,
  GROWTHBOOK_CLIENT_KEY,
  GROWTHBOOK_DECRYPTION_KEY,
  GROWTHBOOK_STREAMING_ENABLED,
  GROWTHBOOK_POLLING_ENABLED,
  GROWTHBOOK_REFRESH_INTERVAL,
} from 'js/constants/config';

// GrowthBook Attribute Definitions - Minimal set for targeting
const GROWTHBOOK_ATTRIBUTES = {
  USER_ID: 'USER_ID',
  ORG_ID: 'ORG_ID',
  IS_ENV_ADMIN: 'IS_ENV_ADMIN',
  IS_ORG_ADMIN: 'IS_ORG_ADMIN',
  IS_LOGGED_IN: 'IS_LOGGED_IN',
} as const;

interface GrowthBookProviderConfig {
  trackingCallback?: (experiment: any, result: any) => void;
}

interface GrowthBookContextType {
  isReady: boolean;
  instance: GrowthBook | null;
  refreshFeatures: () => Promise<void>;
}

const GrowthBookContext = createContext<GrowthBookContextType>({
  isReady: false,
  instance: null,
  refreshFeatures: async () => {},
});

export const useGrowthBookContext = () => useContext(GrowthBookContext);

interface GrowthBookProviderProps {
  children: React.ReactNode;
  config?: GrowthBookProviderConfig;
}

export const GrowthBookProvider: React.FC<GrowthBookProviderProps> = ({
  children,
  config = {},
}) => {
  const user = useCurrentUser();
  const currentOrg = useCurrentOrg();
  const [isReady, setIsReady] = useState(false);

  // GrowthBook configuration logic
  const isGrowthBookEnabled = Boolean(GROWTHBOOK_API_HOST && GROWTHBOOK_CLIENT_KEY);
  const useEncryption = Boolean(GROWTHBOOK_DECRYPTION_KEY);
  const enableDevMode = process.env.NODE_ENV === 'development';
  const enableStreaming = GROWTHBOOK_STREAMING_ENABLED;
  const enablePolling = GROWTHBOOK_POLLING_ENABLED;
  const refreshIntervalMs = GROWTHBOOK_REFRESH_INTERVAL;

  // Create tracking callback
  const defaultTrackingCallback = useMemo(
    () => (experiment: any, result: any) => {
      try {
        // Only track if experiment and result are valid
        if (!experiment?.key || !result?.key) {
          if (enableDevMode) {
            console.warn('GrowthBook: Invalid experiment or result data', {
              experiment,
              result,
            });
          }
          return;
        }

        // Use AnalyticsUtils for consistent tracking
        AnalyticsUtils.captureCustomEvent(
          'growthbook_experiment_viewed',
          {
            experiment_id: experiment.key,
            variation_id: result.key,
            variation_name: result.key,
            experiment_name: experiment.key,
            user_id: user?.id,
            org_id: currentOrg?.id,
            org_name: currentOrg?.name,
            source: 'growthbook',
          },
          {
            forceCapture: true, // Always capture experiment events
            maxProperties: 20, // Allow more properties for experiment tracking
          },
        );

        if (enableDevMode) {
          console.log('GrowthBook Experiment Viewed:', {
            experimentId: experiment.key,
            variationId: result.key,
            userId: user?.id,
            orgId: currentOrg?.id,
            orgName: currentOrg?.name,
          });
        }
      } catch (error) {
        // Never let tracking errors crash the app
        console.warn('GrowthBook: Tracking callback failed:', error);
      }
    },
    [user?.id, currentOrg?.id, currentOrg?.name, enableDevMode],
  );

  const trackingCallback = useMemo(
    () => config.trackingCallback || defaultTrackingCallback,
    [config.trackingCallback, defaultTrackingCallback],
  );

  // Create GrowthBook instance with user and org context
  const growthBookInstance = useMemo(() => {
    // Always create a GrowthBook instance, even when disabled
    // This ensures GBProvider can wrap children and hooks don't fail
    const gb = new GrowthBook({
      apiHost: GROWTHBOOK_API_HOST || 'https://cdn.growthbook.io',
      clientKey: GROWTHBOOK_CLIENT_KEY || '',
      decryptionKey: useEncryption ? GROWTHBOOK_DECRYPTION_KEY : undefined,
      enableDevMode,
      trackingCallback,
      attributes: {},
    });

    if (!isGrowthBookEnabled) {
      if (enableDevMode) {
        console.log(
          'GrowthBook: Disabled - API host or client key not provided. All feature flags will return default values.',
        );
      }
      // Don't set global instance when disabled
      return gb;
    }

    // Set global instance for utility functions
    setGlobalGrowthBookInstance(gb);

    // Log configuration for debugging
    if (enableDevMode) {
      console.log(
        `GrowthBook: Enabled with ${GROWTHBOOK_DECRYPTION_KEY ? 'encrypted' : 'plain text'} payload`,
      );
      console.log(`GrowthBook: API Host: ${GROWTHBOOK_API_HOST}`);
      console.log(`GrowthBook: Client Key: ${GROWTHBOOK_CLIENT_KEY?.substring(0, 10)}...`);
    }

    return gb;
  }, [isGrowthBookEnabled, trackingCallback, enableDevMode, useEncryption]);

  // Track previous user/org for detecting significant changes (internal state, not GrowthBook attributes)
  const [previousContext, setPreviousContext] = useState<{
    userId?: number;
    orgId?: number;
    loggedIn: boolean;
  }>({
    userId: undefined,
    orgId: undefined,
    loggedIn: false,
  });

  // Update attributes when user or org changes
  useEffect(() => {
    if (!isGrowthBookEnabled || !growthBookInstance) return;

    const currentUserId = user?.id;
    const currentOrgId = currentOrg?.id;
    const currentLoggedIn = !!user;

    // Minimal attributes for targeting
    const attributes = user
      ? {
          [GROWTHBOOK_ATTRIBUTES.USER_ID]: currentUserId,
          [GROWTHBOOK_ATTRIBUTES.ORG_ID]: String(currentOrgId),
          [GROWTHBOOK_ATTRIBUTES.IS_ENV_ADMIN]: user?.super_user || false,
          [GROWTHBOOK_ATTRIBUTES.IS_ORG_ADMIN]: user?.isOrgAdmin || false,
          [GROWTHBOOK_ATTRIBUTES.IS_LOGGED_IN]: true,
        }
      : {
          [GROWTHBOOK_ATTRIBUTES.IS_LOGGED_IN]: false,
        };

    // attributes is already defined above

    // Detect significant context changes that require feature refresh
    const significantChange =
      previousContext.userId !== currentUserId ||
      previousContext.orgId !== currentOrgId ||
      previousContext.loggedIn !== currentLoggedIn;

    // Update attributes (this triggers automatic feature re-evaluation)
    growthBookInstance.setAttributes(attributes);

    // For significant changes, also refresh features explicitly to ensure fresh evaluation
    if (significantChange) {
      if (enableDevMode) {
        console.log('GrowthBook: Significant context change detected, refreshing features', {
          previous: previousContext,
          current: {
            userId: currentUserId,
            orgId: currentOrgId,
            loggedIn: currentLoggedIn,
          },
          attributes,
        });
      }

      // Explicit feature refresh for critical context changes
      growthBookInstance.refreshFeatures().catch((error) => {
        console.warn('GrowthBook: Feature refresh failed after context change:', error);
      });

      // Update tracking
      setPreviousContext({
        userId: currentUserId,
        orgId: currentOrgId,
        loggedIn: currentLoggedIn,
      });
    }
  }, [growthBookInstance, user, currentOrg, isGrowthBookEnabled, previousContext]);

  // Initialize GrowthBook
  useEffect(() => {
    if (!isGrowthBookEnabled) {
      // GrowthBook is disabled, set ready to true so hooks return default values
      // Don't initialize the instance, just mark as ready
      setIsReady(true);
      // Dispatch event for consistency
      window.dispatchEvent(new CustomEvent('growthbook:ready'));
      return;
    }

    if (!growthBookInstance) return;

    const initializeGrowthBook = async () => {
      try {
        await growthBookInstance.init({
          timeout: 2000, // 2 second timeout
          streaming: enableStreaming, // Configurable streaming
        });

        // Force feature refresh on initial load to ensure fresh evaluation
        if (enableDevMode) {
          console.log('GrowthBook: Initial load - refreshing features');
        }
        await growthBookInstance.refreshFeatures();

        // Set ready after refresh to ensure features are loaded
        setIsReady(true);

        // Dispatch event that GrowthBook is ready (for non-React consumers)
        window.dispatchEvent(new CustomEvent('growthbook:ready'));
      } catch (error) {
        console.error('GrowthBook initialization failed:', error);
        // Still mark as ready so the app proceeds with defaultValues rather than
        // staying permanently blocked with isReady=false
        setIsReady(true);
      }
    };

    initializeGrowthBook();

    // Setup polling for feature refresh (if enabled and streaming disabled)
    let refreshInterval: NodeJS.Timeout | null = null;
    if (enablePolling && !enableStreaming && refreshIntervalMs > 0) {
      refreshInterval = setInterval(() => {
        growthBookInstance.refreshFeatures().catch((error) => {
          console.warn('GrowthBook refresh failed:', error);
        });
      }, refreshIntervalMs);
    }

    // Cleanup on unmount
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
      if (growthBookInstance) {
        growthBookInstance.destroy();
      }
    };
  }, [
    growthBookInstance,
    isGrowthBookEnabled,
    enablePolling,
    enableStreaming,
    refreshIntervalMs,
    enableDevMode,
  ]);

  // Manual feature refresh method
  const refreshFeatures = async (): Promise<void> => {
    if (!isGrowthBookEnabled || !growthBookInstance) {
      return;
    }

    try {
      await growthBookInstance.refreshFeatures();
      if (enableDevMode) {
        console.log('GrowthBook: Features manually refreshed');
      }
    } catch (error) {
      console.warn('GrowthBook: Manual feature refresh failed:', error);
    }
  };

  const contextValue: GrowthBookContextType = {
    isReady,
    instance: isGrowthBookEnabled ? growthBookInstance : null,
    refreshFeatures,
  };

  // Always wrap with GBProvider to ensure hooks from @growthbook/growthbook-react work
  // Even when disabled, we need the provider present
  return (
    <GrowthBookContext.Provider value={contextValue}>
      <GBProvider growthbook={growthBookInstance}>{children}</GBProvider>
    </GrowthBookContext.Provider>
  );
};

export default GrowthBookProvider;
